/**
 * @class Filter
 *
 * @classdesc Represents a class for creating node filters. Implementation and
 * definitions based on https://github.com/mapbox/feature-filter.
 */
export class FilterCreator {
    /**
     * Create a filter from a filter expression.
     *
     * @description The following filters are supported:
     *
     * Comparison
     * `==`
     * `!=`
     * `<`
     * `<=`
     * `>`
     * `>=`
     *
     * Set membership
     * `in`
     * `!in`
     *
     * Combining
     * `all`
     *
     * @param {FilterExpression} filter - Comparison, set membership or combinding filter
     * expression.
     * @returns {FilterFunction} Function taking a node and returning a boolean that
     * indicates whether the node passed the test or not.
     */
    createFilter(filter) {
        return new Function("node", "return " + this._compile(filter) + ";");
    }
    _compile(filter) {
        if (filter == null || filter.length <= 1) {
            return "true";
        }
        const operator = filter[0];
        const operation = operator === "=="
            ? this._compileComparisonOp("===", filter[1], filter[2], false)
            : operator === "!="
                ? this._compileComparisonOp("!==", filter[1], filter[2], false)
                : operator === ">" || operator === ">=" || operator === "<" || operator === "<="
                    ? this._compileComparisonOp(operator, filter[1], filter[2], true)
                    : operator === "in"
                        ? this._compileInOp(filter[1], filter.slice(2))
                        : operator === "!in"
                            ? this._compileNegation(this._compileInOp(filter[1], filter.slice(2)))
                            : operator === "all"
                                ? this._compileLogicalOp(filter.slice(1), "&&")
                                : "true";
        return "(" + operation + ")";
    }
    _compare(a, b) {
        return a < b ? -1 : a > b ? 1 : 0;
    }
    _compileComparisonOp(operator, property, value, checkType) {
        const left = this._compilePropertyReference(property);
        const right = JSON.stringify(value);
        return (checkType ? "typeof " + left + "===typeof " + right + "&&" : "") + left + operator + right;
    }
    _compileInOp(property, values) {
        const compare = this._compare;
        const left = JSON.stringify(values.sort(compare));
        const right = this._compilePropertyReference(property);
        return left + ".indexOf(" + right + ")!==-1";
    }
    _compileLogicalOp(filters, operator) {
        const compile = this._compile.bind(this);
        return filters.map(compile).join(operator);
    }
    _compileNegation(expression) {
        return "!(" + expression + ")";
    }
    _compilePropertyReference(property) {
        const index = property.split(".").reduce((acc, value) => acc + `[${JSON.stringify(value)}]`, "");
        return `node${index}`;
    }
}
//# sourceMappingURL=FilterCreator.js.map